import * as THREE from 'three';
import {AbstractPointSnapStrategy} from './AbstractPointSnapStrategy';
import {PointSnapStrategyType} from './PointSnapStrategyType';
import type {PointSnapRequest, PointSnapResult} from './PointSnapTypes';
import {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import type {SketchCircleObject} from '../../mesh/sketch/SketchCircleObject';
import type {SketchArcObject} from '../../mesh/sketch/SketchArcObject';
import type {SketchEllipseObject} from '../../mesh/sketch/SketchEllipseObject';
import {isSketchCircleObject} from '../../mesh/guards/isSketchCircleObject';
import {isSketchArcObject} from '../../mesh/guards/isSketchArcObject';
import {isSketchEllipseObject} from '../../mesh/guards/isSketchEllipseObject';
import {isSketchLineObject} from '../../mesh/guards/isSketchLineObject';
import {GeometryInteractionState} from '../interactionState/GeometryInteractionState';
import {QuickSnapMode} from './QuickSnapMode';

/** 支持象限点显示的形状类型联合。 */
type QuadrantShape = SketchCircleObject | SketchArcObject | SketchEllipseObject;

/**
 * 象限点捕捉策略。
 * 说明：
 * 1. 鼠标悬停在圆/圆弧/椭圆边缘时，显示该形状所有有效象限点（isActiveForSnap=true）；
 * 2. 鼠标接近某象限点时，捕捉到该点并在右下角显示象限点图标；
 * 3. 使用 currentlyVisiblePoints 集合做帧间差量更新，避免遗漏隐藏；
 * 4. 通过 reset() 在绘制结束时清理所有可见象限点。
 */
export class QuadrantPointSnapStrategy extends AbstractPointSnapStrategy {
    /** 策略类型。 */
    public readonly strategyType: PointSnapStrategyType = PointSnapStrategyType.Quadrant;

    /**
     * 象限点捕捉权重。
     * 说明：优先级低于圆心（90），高于中点（80），取值 85。
     */
    private static readonly WEIGHT = 85;

    /** 快速捕捉模式权重，确保快速象限点胜出普通捕捉结果。 */
    private static readonly QUICK_SNAP_WEIGHT = 200;

    /**
     * 当前帧正在显示的象限点集合。
     * 说明：每帧通过差量计算决定 show/hide，保证状态干净。
     */
    private currentlyVisiblePoints: Set<SketchPointObject> = new Set();

    /**
     * 最后一次真正悬浮的圆/圆弧/椭圆形状。
     * 说明：鼠标移出后继续显示该形状的象限点，直到移入其他几何体才切换。
     */
    private lastHoveredShape: QuadrantShape | null = null;

    /** 当前吸附高亮的象限点对象。 */
    private snappedHighlightedPoint: SketchPointObject | null = null;

    /** 当前请求复用的模型象限点候选数组。 */
    private readonly transientModelQuadrantPoints: SketchPointObject[] = [];

    /** 快速捕捉模式下当前高亮的圆/圆弧/椭圆对象。 */
    private quickSnapHighlightedShape: QuadrantShape | null = null;

    /**
     * 执行象限点捕捉求解。
     * 流程：
     * 1. 遍历活跃草图组的象限点集合，找到最近且在捕捉阈值内的点；
     * 2. 检测鼠标是否悬停在圆/圆弧/椭圆边缘，若是则显示该形状所有有效象限点；
     * 3. 合并上述两类点为本帧可见集合，与上帧差量更新 visible；
     * 4. 有捕捉命中时返回结果，否则返回 null。
     * @param request 点捕捉请求参数。
     * @returns 最近象限点的捕捉结果；无命中时返回 null。
     */
    public resolve(request: PointSnapRequest): PointSnapResult | null {
        
        if (request.quickSnapMode === QuickSnapMode.Quadrant) {
            return this.resolveQuickSnap(request);
        }
        

        // 普通模式不应保留快速捕捉的边线高亮。
        this.clearQuickSnapShapeHighlight();

        const quadrantPoints = this.getActiveQuadrantPoints();

        
        let bestResult: PointSnapResult | null = null;
        let bestDist = this.snapPixelThreshold;
        let snappedPoint: SketchPointObject | null = null;

        this.transientModelQuadrantPoints.length = 0;
        for (const point of request.transientPointObjects ?? []) {
            if (
                !point.isSketchSessionModelEdgeQuadrant
                || !point.visible
                || this.isSnapTargetExcluded(request, point)
            ) {
                continue;
            }
            this.transientModelQuadrantPoints.push(point);
            const worldPos = point.getPosition();
            const screenPos = this.projectWorldToScreen(worldPos);
            if (!screenPos) {
                continue;
            }
            const dist = screenPos.distanceTo(request.screenPosition);
            if (dist < bestDist) {
                bestDist = dist;
                snappedPoint = point;
                bestResult = {
                    strategyType: this.strategyType,
                    position: worldPos.clone(),
                    targetObject: point,
                    weight: QuadrantPointSnapStrategy.WEIGHT,
                };
            }
        }

        if (quadrantPoints !== null) {
            for (const point of quadrantPoints) {
                if (this.isSnapTargetExcluded(request, point)) {
                    continue;
                }
                // 跳过不在弧范围内的圆弧象限点。
                if (!point.isActiveForSnap) {
                    continue;
                }
                const worldPos = point.getPosition();
                const screenPos = this.projectWorldToScreen(worldPos);
                if (screenPos === null) {
                    continue;
                }
                const dist = screenPos.distanceTo(request.screenPosition);
                if (dist < bestDist) {
                    bestDist = dist;
                    snappedPoint = point;
                    bestResult = {
                        strategyType: this.strategyType,
                        position: worldPos.clone(),
                        targetObject: point,
                        weight: QuadrantPointSnapStrategy.WEIGHT,
                    };
                }
            }
        }
        

        
        // 检测当前悬停形状，获取需要显示的象限点。
        const hoveredPoints = this.computeHoveredShapePoints(request, request.hoveredObject ?? null);

        // 构建本帧应可见的象限点集合（悬停点 ∪ 捕捉点）。
        const nextVisible = new Set<SketchPointObject>(hoveredPoints);
        this.transientModelQuadrantPoints.forEach((point) => nextVisible.add(point));
        if (snappedPoint !== null) {
            nextVisible.add(snappedPoint);
        }

        this.syncVisibleQuadrantPoints(nextVisible, bestResult?.targetObject ?? null);
        

        return bestResult;
    }

    /**
     * 快速象限点捕捉求解。
     *
     * 设计说明：
     * 1. QuickSnap 是显式用户意图，不能沿用普通模式的像素阈值；
     * 2. 这里直接扫描活跃草图内所有圆、圆弧和椭圆的有效象限点；
     * 3. 按屏幕距离选择最近象限点，同时显示该形状的整组有效象限点。
     *
     * @param request 点捕捉请求。
     * @returns 最近有效象限点捕捉结果；无有效象限点时返回 null。
     */
    private resolveQuickSnap(request: PointSnapRequest): PointSnapResult | null {
        const group = this.viewer?.sketchGroupManager?.activeSketchGroup;
        if (!group) {
            this.clearQuickSnapShapeHighlight();
            this.syncVisibleQuadrantPoints(new Set(), null);
            return null;
        }

        let bestShape: QuadrantShape | null = null;
        let bestPoint: SketchPointObject | null = null;
        let bestScreenDistance = Infinity;

        for (const edge of group.getSketchEdgeObjects()) {
            if (this.isSnapTargetExcluded(request, edge)) {
                continue;
            }
            if (!isSketchCircleObject(edge) && !isSketchArcObject(edge) && !isSketchEllipseObject(edge)) {
                continue;
            }

            const shape = edge as QuadrantShape;
            for (const point of this.getShapeQuadrantPoints(request, shape)) {
                const worldPos = point.getPosition();
                const screenPos = this.projectWorldToScreen(worldPos);
                if (screenPos === null) {
                    continue;
                }

                const screenDistance = screenPos.distanceTo(request.screenPosition);
                if (screenDistance < bestScreenDistance) {
                    bestScreenDistance = screenDistance;
                    bestShape = shape;
                    bestPoint = point;
                }
            }
        }

        if (bestShape === null || bestPoint === null) {
            this.clearQuickSnapShapeHighlight();
            this.syncVisibleQuadrantPoints(new Set(), null);
            return null;
        }

        this.lastHoveredShape = bestShape;
        this.syncQuickSnapShapeHighlight(bestShape);

        const visiblePoints = new Set<SketchPointObject>(
            this.getShapeQuadrantPoints(request, bestShape),
        );
        visiblePoints.add(bestPoint);
        this.syncVisibleQuadrantPoints(visiblePoints, bestPoint);

        return {
            strategyType: this.strategyType,
            position: bestPoint.getPosition().clone(),
            targetObject: bestPoint,
            weight: QuadrantPointSnapStrategy.QUICK_SNAP_WEIGHT,
            showIndicator: true,
        };
    }

    /**
     * 重置象限点显示状态。
     * 说明：在绘制结束或策略停用时调用，确保不残留可见的象限点对象。
     */
    public reset(): void {
        for (const p of this.currentlyVisiblePoints) {
            this.viewer?.interactionStateManager?.setSketchObjectVisibilityHint?.(p, false);
            p.visible = false;
        }
        this.currentlyVisiblePoints.clear();
        this.lastHoveredShape = null;
        this.clearQuickSnapShapeHighlight();
        this.syncSnapHoverState(null);
    }

    /**
     * 清除象限点预览状态。
     */
    public override clearPreview(): void {
        this.reset();
    }

    /**
     * 检测鼠标当前悬停的圆/圆弧/椭圆，返回其所有有效象限点。
     * 说明：
     * - 若 hoveredObject 是当前草图中的圆/弧/椭圆，则更新 lastHoveredShape 并返回其象限点；
     * - 若 hoveredObject 是当前草图中的其它边（如直线），则立即清空 lastHoveredShape；
     * - 若 hoveredObject 为空或不是当前草图边，则继续使用 lastHoveredShape 的象限点（粘滞显示）；
     * - 这样可与中点策略保持一致：移出空白时保留，移入新边时切换或清空旧显示。
     * @param request 点捕捉请求，用于排除当前变换域对象。
     * @param hoveredObject 当前悬浮对象（由 EntitySelectionManager 提供）。
     * @returns 需要显示的象限点列表。
     */
    private computeHoveredShapePoints(request: PointSnapRequest, hoveredObject: THREE.Object3D | null): SketchPointObject[] {
        const group = this.viewer?.sketchGroupManager?.activeSketchGroup;
        if (group && hoveredObject !== null) {
            const isEdge = isSketchLineObject(hoveredObject)
                || isSketchArcObject(hoveredObject)
                || isSketchCircleObject(hoveredObject)
                || isSketchEllipseObject(hoveredObject);

            if (isEdge) {
                const matchedEdge = Array.from(group.getSketchEdgeObjects())
                    .find((edge) => edge === hoveredObject) ?? null;

                if (matchedEdge !== null) {
                    if (this.isSnapTargetExcluded(request, matchedEdge)) {
                        this.lastHoveredShape = null;
                        return [];
                    }
                    if (
                        isSketchCircleObject(matchedEdge)
                        || isSketchArcObject(matchedEdge)
                        || isSketchEllipseObject(matchedEdge)
                    ) {
                        // 移入新的象限点支持形状时，立即切换显示目标。
                        this.lastHoveredShape = matchedEdge as QuadrantShape;
                    } else {
                        // 移入其它边时应隐藏旧形状的象限点，行为与中点切到新边一致。
                        this.lastHoveredShape = null;
                    }
                }
            }
        }

        // 使用最后悬浮形状（粘滞显示，移出时保持可见）。
        if (!this.lastHoveredShape) {
            return [];
        }
        if (this.isSnapTargetExcluded(request, this.lastHoveredShape)) {
            this.lastHoveredShape = null;
            return [];
        }
        return this.getShapeQuadrantPoints(request, this.lastHoveredShape);
    }

    /**
     * 获取形状的所有有效象限点（仅 isActiveForSnap=true 的点）。
     * @param request 点捕捉请求，用于排除当前变换域对象。
     * @param shape 圆、圆弧或椭圆对象。
     * @returns 有效象限点数组。
     */
    private getShapeQuadrantPoints(request: PointSnapRequest, shape: QuadrantShape): SketchPointObject[] {
        let all: Iterable<SketchPointObject>;
        if (isSketchCircleObject(shape)) {
            all = shape.getQuadrantPoints();
        } else if (isSketchArcObject(shape)) {
            all = shape.getQuadrantPoints();
        } else {
            // 椭圆象限点（isSketchEllipseObject）。
            all = shape.getQuadrantPoints();
        }
        return Array.from(all).filter((point) => {
            return point.isActiveForSnap && !this.isSnapTargetExcluded(request, point);
        });
    }

    /**
     * 同步本帧需要显示的象限点集合。
     *
     * @param nextVisible 本帧应保持可见的象限点集合。
     * @param targetObject 当前捕捉命中的目标对象。
     */
    private syncVisibleQuadrantPoints(
        nextVisible: Set<SketchPointObject>,
        targetObject: PointSnapResult['targetObject'] | null,
    ): void {
        // 差量更新：仅对退出集合的点执行隐藏。
        // 说明：
        // 1. 其它点类策略在清理高亮时，可能会把象限点恢复到默认隐藏态；
        // 2. 若这里只对“新增集合成员”执行 visible=true，则已在集合中的粘滞象限点不会被重新点亮；
        // 3. 因此保留差量隐藏，同时对当前帧应可见集合做一次显式保活，行为与中点策略一致。
        for (const point of this.currentlyVisiblePoints) {
            if (!nextVisible.has(point)) {
                this.viewer?.interactionStateManager?.setSketchObjectVisibilityHint?.(point, false);
                point.visible = false;
            }
        }
        for (const point of nextVisible) {
            this.viewer?.interactionStateManager?.setSketchObjectVisibilityHint?.(point, true);
            point.visible = true;
        }
        this.currentlyVisiblePoints = nextVisible;
        this.syncSnapHoverState(targetObject);

        // 交互态写入 Hover 时会触发默认态刷新，默认态会把未命中的象限点按拓扑规则隐藏。
        // 因此必须在 Hover 同步之后再次保活本帧可见集合，确保同一形状上的其它象限点不被覆盖掉。
        for (const point of nextVisible) {
            point.visible = true;
        }
    }

    /**
     * 同步快速捕捉模式下的源形状高亮。
     *
     * @param shape 当前快速象限点捕捉选中的源形状。
     */
    private syncQuickSnapShapeHighlight(shape: QuadrantShape): void {
        if (this.quickSnapHighlightedShape === shape) {
            return;
        }

        this.clearQuickSnapShapeHighlight();
        this.viewer?.interactionStateManager?.setSketchObjectState(
            shape,
            GeometryInteractionState.Hover,
        );
        this.quickSnapHighlightedShape = shape;
    }

    /**
     * 清理快速象限点捕捉的源形状高亮。
     */
    private clearQuickSnapShapeHighlight(): void {
        if (this.quickSnapHighlightedShape === null) {
            return;
        }

        this.viewer?.interactionStateManager?.restoreSketchObjectState(this.quickSnapHighlightedShape);
        this.quickSnapHighlightedShape = null;
    }

    /**
     * 同步当前命中的象限点移入材质状态。
     * @param targetObject 当前捕捉目标对象。
     */
    private syncSnapHoverState(targetObject: PointSnapResult['targetObject'] | null): void {
        const point = targetObject instanceof SketchPointObject ? targetObject : null;
        if (point === null) {
            if (this.snappedHighlightedPoint !== null) {
                this.viewer?.interactionStateManager?.restoreSketchObjectState(this.snappedHighlightedPoint);
                this.snappedHighlightedPoint = null;
            }
            return;
        }

        if (this.snappedHighlightedPoint !== point) {
            if (this.snappedHighlightedPoint !== null) {
                this.viewer?.interactionStateManager?.restoreSketchObjectState(this.snappedHighlightedPoint);
            }
            this.snappedHighlightedPoint = point;
        }

        // 命中象限点本体时，外层交互态可能已把该点恢复为默认隐藏；
        // 这里需要像中心点策略一样显式保活，避免“鼠标移到点上后反而消失”。
        point.visible = true;
        this.viewer?.interactionStateManager?.setSketchObjectState(
            point,
            GeometryInteractionState.Hover,
        );
    }
}
